<template>
  <div :id="id"
       ref="node"
       class="node"
       :class="{
        hovered: isHovered,
        active: isActive,
        paled: isDimmed,
        highlighted: isHighlighted,
        focused: isFocused,
        trackable: isTrackable,
        wide: layout.width >= (285 - 4)
      }"
       :style="{
         left: layout.left + 'px',
         top: nodeBaseTop + layout.top + 'px',
         width: layout.width + 'px',
         height: layout.height + 'px'
       }"
       @mouseenter="isHovered = true"
       @mouseleave="isHovered = false"
       @click="onNodeAreaClick">
    <svg v-if="!isThruHighlighting || !!thruData"
         class="dropoff-path"
         width="50"
         :height="layout.height + nodeBottomMargin"
         :style="{ left: layout.width - 10 + 'px' }">
      <ba-node-dropoff-path :nodeData="nodeData"
                            :thruData="thruData"
                            :layout="nodeData._layout" />
    </svg>
    <div class="area"
         :class="[{ hustled: 20 > layout.height }, { 'no-category': journeyOptions.matchType === 'exact' }]"
         :style="{ height: layout.height + 'px', background: backgroundColor }">
      <span class="category-icon small" v-if="!isCustomNode && nodeData._id !== '-1' && journeyOptions.matchType !== 'exact'"
         :class="[20 > layout.height ? 'hustled' : null, nodeInfo && nodeInfo.AICategory1 && nodeInfo.AICategory1.length !== 0 ? `${nodeInfo.AICategory1}` : 'etc']">
         <i class="icon" :class="nodeInfo && nodeInfo.AICategory1 && nodeInfo.AICategory1.length !== 0 ? `icon-${nodeInfo.AICategory1}` : 'icon-etc'"></i>
      </span>
      <div class="text-area">
        <template v-if="!isCustomNode">
          <transition name="fade">
            <span v-if="nodeData._id === '-1'"
                  class="etc">{{ $t('trend.other') }}</span>
            <span v-else-if="grouping"
                  class="group-name">
            <span class="name">{{ groupName }}</span>
              <i v-if="nodeInfo.bookmark"
                 class="icon icon--favorite-small" />
            </span>
            <ba-node-link v-else-if="!grouping && nodeInfo"
                          :pageData="nodeInfo"
                          :displayDefaultBookmark="true"
                          @onMouseEnter="isHovered = false"
                          @onMouseLeave="isHovered = true" />
          </transition>
        </template>
        <template v-else>
          <span v-if="nodeData._id === '-1'" class="etc">{{ $t('trend.other') }}</span>
          <span class="custom" v-else>{{ nodeInfo.customId === 0 ? $t('customTag.untaggedLabel') : nodeInfo.customValue}}</span>
        </template>
      </div>
    </div>
    <div class="detail-node" :class="{ trackable: isTrackable }">
      <ba-tracking-bar
          v-btf-tooltip:top="{contents: nodeData._id === '-1' ? $t('tracking.etcNode') : null, size: '300px'}"
          v-if="isTrackable"
          :disabled="nodeData._id === '-1'"
          :total="nodeData.sessCnt"
          :member="nodeData.memberSessCnt"
          :login="nodeData.loggedInSessCnt"
          :newLogin="nodeData.newlyLoggedInSessCnt"
          @onMouseEnter="isHovered = false"
          @onMouseLeave="isHovered = true"
          @onClick="$emit('onShowTrackingLayer', sequenceIndex, nodeIndex)"/>
      <div class="item-wrap">
        <span class="item session"
              :class="{ 'with-thru-data': isThruHighlighting, 'invalid-value': nodeData.sessCntIsInaccurate }">
          <i class="icon icon--ba-session-transparent"></i>
          <span v-if="isThruHighlighting"
                class="thru-data" :class="{'invalid-value': thruData && thruData.sessCntIsInaccurate}">
            {{ getComputedThruSessCnt | letterThousandMillion(0) }}
          </span>
            {{ getComputedSessCnt | letterThousandMillion(0) }}
        </span>
        <span v-if="!isThruHighlighting && visibleOptions.refresh"
              class="item refresh">
          <i class="icon icon--refresh"></i>
          {{ getPercentage(nodeData.reloadCnt) }}
        </span>
      </div>
      <span v-if="!isThruHighlighting && visibleOptions.duration"
            class="item duration">
        <i class="icon icon--hourglass-1"></i>
        {{ timeString }}
      </span>
      <span v-if="!isThruHighlighting && visibleOptions.rollback"
            class="item rollback">
        <i class="icon icon--rollback-new"></i>
        {{ getPercentage(nodeData.rollCnt) }}
      </span>
      <span v-if="(!isThruHighlighting || !!thruData) && visibleOptions.dropOff"
            class="item dropoff"
            :style="{ left: dropoffPosition[0] + 'px', top: dropoffPosition[1] + 'px' }">
        {{ getPercentage((thruData || nodeData).dropCnt || 0, true) }}
      </span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { onlySubdomain, getTimeString } from '@/libs/utils'

import BaNodeLink from '@/components/trend/NodeLink'
import BaNodeDropoffPath from '@/components/trend/NodeDropoffPath'
import BaTrackingBar from '@/components/journey/TrackingBar.vue'

export default {
  name: 'BaNodeItem',
  components: {
    BaNodeLink,
    BaNodeDropoffPath,
    BaTrackingBar
  },
  props: {
    id: {
      type: String,
      required: true
    },
    sequenceIndex: {
      type: Number,
      required: true
    },
    nodeIndex: {
      type: Number,
      required: true
    },
    nodeData: {
      type: Object,
      required: true
    },
    thruData: {
      type: Object
    },
    nodeInfo: {
      type: Object
    },
    layout: {
      type: Object,
      required: true
    },
    nodeBaseTop: {
      type: Number,
      required: true
    },
    nodeBottomMargin: {
      type: Number,
      required: true
    },
    isTrackable: {
      default: false
    },
    tracking: {
      default: false
    },
    grouping: {
      type: Boolean,
      required: true
    },
    group: {
      type: String,
      required: true
    },
    visibleOptions: {
      type: Object,
      required: true
    },
    isActive: {
      type: Boolean,
      required: true
    },
    isFocused: {
      type: Boolean,
      required: true
    },
    isDimmed: {
      type: Boolean,
      required: true
    },
    isHighlighted: {
      type: Boolean,
      required: true
    },
    isThruHighlighting: {
      type: Boolean,
      required: true
    },
    isCustomNode: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isHovered: false
    }
  },
  watch: {
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'domain',
      'originTrendData',
      'isSampling'
    ]),
    getComputedThruSessCnt () {
      if (!this.thruData) {
        return 0
      }
      return this.thruData.sessCntIsInaccurate ? this.$t('common.invalidValue') : this.thruData.sessCnt
    },
    getComputedSessCnt () {
      return this.nodeData.sessCntIsInaccurate ? this.$t('common.invalidValue') : this.nodeData.sessCnt
    },
    isSamplingOptions () {
      return this.isSampling && !this.journeyOptions.survey
    },
    hasGroup () {
      return this.nodeInfo && this.nodeInfo.tagInfo && this.nodeInfo.tagInfo[this.group]
    },
    groupName () {
      return this.nodeInfo ? onlySubdomain(this.nodeInfo.tagInfo[this.group].tagName || this.$t('group.upcoming'), this.journeyOptions.domain) : this.$t('trend.other')
    },
    backgroundColor () {
      if (this.grouping) {
        if (this.nodeInfo) {
          return this.hasGroup ? this.nodeInfo.tagInfo[this.group].colorHex || '#bbb' : '#bbb'
        } else {
          return '#666'
        }
      }

      if (this.isCustomNode) {
        if (this.nodeData._id === '-1') {
          return '#666'
        } else {
          return this.nodeInfo.customColor
        }
      }
      return ''
    },
    timeString () {
      return getTimeString(this.nodeData.sessTime)
    },
    dropoffPosition () {
      const nodeData = this.nodeData
      const dropCnt = (this.thruData || nodeData).dropCnt || 0
      const layout = this.layout
      const height = layout.height * dropCnt / nodeData._realOutSessCnt
      const minHeight = 20
      const scale = Math.max(0.1, Math.min(1, height / minHeight))
      const translate = [layout.width + 20 * scale + 7, layout.height - height + 15 * scale]
      return translate
    }
  },
  methods: {
    onNodeAreaClick (e) {
      this.$emit('onNodeAreaClick', e, this.sequenceIndex, this.nodeIndex)
    },
    getPercentage (value, isNotOver) {
      if (isNotOver) {
        if (((value / this.nodeData.sessCnt) * 100) > 100) {
          return '100.0%'
        }
      }
      return ((value / this.nodeData.sessCnt) * 100).toFixed(2) + '%'
    }
  }
}
</script>

<style lang="scss" scoped>
.node {
  position: absolute;
  cursor: pointer;
  transform: translate3d(0, 0, 0);
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  &.active,
  &.hovered,
  &:hover {
    z-index: 3;
  }

  &:before,
  &:after {
    position: absolute;
    content: '';
    background-color: #f4f4f1;
    opacity: 0;
    z-index: 2;
    transition: opacity 0.3s ease-out;
    pointer-events: none;
  }
  &:before {
    left: -3px;
    right: -3px;
    top: -3px;
    bottom: -3px;
    border-radius: 8px;
  }
  &:after {
    left: 0;
    right: 0;
    top: calc(100% + 3px);
    height: 70px;
  }

  &.paled:not(:hover):not(.active):not(.focused) {
    &:before,
    &:after {
      opacity: 0.45;
    }
  }
  &.focused {
    z-index: 5;
  }
  &.highlighted {
    .area {
      box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 0 0 2px #333, 0 0 0 1px rgba(0, 0, 0, 0.05);
    }
  }
  &.active,
  &.hovered {
    .area {
      box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 0 0 2px #0274FF, 0 0 0 1px rgba(0, 0, 0, 0.05);
    }
  }
}

.dropoff-path {
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;

  & /deep/ {
    path {
      transition: opacity 0.2s ease-out;
    }
    path.dim {
      opacity: 0.3
    }
  }
}

.area {
  position: relative;
  margin: -1px -1px 0;
  border-radius: 8px;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 0 0 0 #0274FF, 0 0 0 1px rgba(0, 0, 0, 0.05);
  border: 1px solid #fff;
  box-sizing: content-box;
  background-color: #0e90a5;
  overflow: visible;
  z-index: 1;
  transition: box-shadow 0.15s ease-out, background 0.3s ease-out;
  display: flex;
  align-items: center;

  .link-wrap {
    left: 34px;
  }

  &.hustled {
    .link-wrap {
      left: 30px;
    }
  }
  &.no-category {
    .link-wrap {
      left: 6px;
    }
  }
}

.category-icon {
  opacity: 1;
  height: 100%;
  display: flex;
  // align-items: center;
  // justify-content: center;
  font-size: 16px;
  width: 28px;
  padding: 2px;
  border-top-left-radius: 7px;
  border-bottom-left-radius: 7px;
  box-sizing: border-box;
  color: #222;

  &.small {
    border-radius: 0;
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
  }

  &.hustled {
    font-size: 14px;
    width: 18px;
    height: 18px;
    border-radius: 3px;
    padding: 2px;
    position: absolute;
    left: 5px;
    right: 0;
    top: calc(100% + 13px);
    transform: translateY(-50%);
  }
}

.text-area {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 100%;

  .custom {
    color: #FFF;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px; /* 100% */

    position: absolute;
    left: 8px;
    top: 50%;
    height: 12px;
    transform: translateY(-50%);
  }

  .etc,
  .group-name {
    position: absolute;
    left: 34px;
    top: 50%;
    height: 20px;
    font-size: 12px;
    font-weight: 500;
    color: #fff;
    line-height: 20px;
    transform: translateY(-50%);
  }
  .etc {
    left: 8px;
  }
  .group-name {
    right: 8px;
    display: flex;

    .name {
      flex: 1;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .icon {
      position: relative;
      right: -4px;
      top: 2px;
    }
  }

  .fade {
    &-enter-active, &-leave-active {
      transition: opacity 0.3s ease-out;
    }

    &-enter, &-leave-to {
      opacity: 0;
    }
  }

  .area.hustled & {
    top: calc(100% + 13px);
    height: auto;

    .etc,
    .group-name {
      color: #666;
    }

    .custom {
      color: #666;
    }

    & /deep/ {
      .link {
        color: #666;
      }
    }
  }
}

.detail-node {
  white-space: normal;
  margin: 0 -2px;

  &.trackable {
    padding-top: 20px;
    .item-wrap {
      flex-direction: row;
    }
  }

  .area.hustled + & {
    padding-top: 21px;
    /deep/ .tracking-bar {
      bottom: -25px;
    }
    &.trackable {
      padding-top: 40px;
    }
  }
  .item-wrap {
    display: flex;
    flex-direction: column;
  }

  .item {
    position: relative;
    width: 105px;
    height: 20px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    color: #008299;
    margin-top: 3px;
    padding-left: 30px;
    box-sizing: border-box;
    display: inline-block;

    &.with-thru-data {
      width: auto;
      color: #89c6d0;

      .thru-data {
        color: #008299;

        &:after {
          content: '/';
          color: #89c6d0;
        }
        &.invalid-value {
          color: #444;
          font-weight: 400;
          &:after {
            color: #444;
          }
        }
      }
    }
    &.invalid-value {
      color: #b1b1b1;
      font-weight: 400;
    }

    &.refresh {
      color: #0074ff;
    }

    &.duration {
      color: #4b8f0d;
    }

    .icon {
      position: absolute;
      left: 8px;
      top: 0;
    }

    &.rollback {
      position: absolute;
      right: -11px;
      top: -20px;
      width: auto;
      font-weight: bold;
      color: #9665e3;
      padding: 0 24px 0 6px;
      border-radius: 4px;
      box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.2), 0 0 4px 0 rgba(0, 0, 0, 0.12);
      background-color: #fff;
      z-index: 3;

      .icon {
        position: absolute;
        left: auto;
        right: 2px;
        top: 0;
      }
    }

    &.dropoff {
      position: absolute;
      left: calc(100% + 24px);
      width: auto;
      height: auto;
      color: #ff4a00;
      line-height: 1;
      margin: 0;
      padding: 0;
      pointer-events: none;
    }
  }

  /deep/ .tracking-bar {
    bottom: -4px;
    left: 4px;
    transform: translateY(100%) scale(0.69);
    transform-origin: left top;
    width: calc(100% - 21px);
    transition: all 0.4s ease;
    z-index: 1;
    &:hover:not(.disabled) {
      transform: translateY(100%) scale(1);
    }
  }
}

.detail-node .item:first-child + .item:not(.dropoff):before,
.node.wide .detail-node .item + .item:not(.dropoff):before {
  position: absolute;
  left: -1px;
  top: 5px;
  width: 0;
  height: 10px;
  content: '';
  border-left: 1px solid #d7d7d7;
}
</style>
